import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, post, tokenStore, tenantStore } from "./api";
import type { User } from "./types";

/**
 * Super-admin impersonation. The admin's auth material (Sanctum session
 * cookie) lives in browser cookies and is never disturbed by us — the
 * impersonation flow only touches localStorage.
 *
 * Storage layout in localStorage:
 *   control.token              — currently-active bearer (used by api.ts)
 *   control.impersonation      — `{ admin_token?, target, started_at }` JSON
 *
 * Sanctum's guard chain prefers a Bearer header when present, falling
 * through to the session cookie otherwise. So writing the impersonation
 * token to `control.token` makes every subsequent request authenticate
 * as the target user; clearing it on stop lets the admin's session
 * cookie reassert.
 *
 * For legacy bearer-only logins (pre-cookie-auth migration), `admin_token`
 * holds the admin's own bearer so we can restore it on stop. Modern
 * cookie-auth logins have no bearer to preserve and `admin_token` is null.
 */

interface ImpersonationTarget {
  id: number;
  name: string;
  email: string;
  organisations?: Array<{ id: number; name: string }>;
}

interface ImpersonationState {
  isImpersonating: boolean;
  target: ImpersonationTarget | null;
  startedAt: string | null;
  expiresAt: string | null;
  /** The real super-admin's email — for the banner. */
  adminEmail: string | null;
}

interface ImpersonationContextValue extends ImpersonationState {
  start: (userId: number) => Promise<void>;
  stop: () => Promise<void>;
}

const KEY = "control.impersonation";

/** Forget any impersonation on sign-out; the banner outlived logout (audit FE-30). */
export function clearStoredImpersonation(): void {
  try { localStorage.removeItem(KEY); } catch { /* storage unavailable */ }
}
const ImpersonationCtx = createContext<ImpersonationContextValue | null>(null);

interface StoredImpersonation {
  /** Admin's bearer token, if they were on legacy bearer auth. Null on cookie auth. */
  admin_token: string | null;
  admin_email: string;
  target: ImpersonationTarget;
  started_at: string;
  expires_at: string | null;
  /** Tenant the *admin* was on, so we can restore it after stopping. */
  admin_tenant_id: number | null;
}

function readStored(): StoredImpersonation | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as StoredImpersonation) : null;
  } catch {
    return null;
  }
}

function writeStored(s: StoredImpersonation | null): void {
  if (s) localStorage.setItem(KEY, JSON.stringify(s));
  else localStorage.removeItem(KEY);
}

export function ImpersonationProvider({
  user, children,
}: {
  user: User | null;
  children: ReactNode;
}) {
  const [stored, setStored] = useState<StoredImpersonation | null>(() => readStored());

  // If localStorage gets out of sync (another tab) re-read on focus.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setStored(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const start = useCallback<ImpersonationContextValue["start"]>(async (userId) => {
    if (!user) throw new Error("Not signed in.");
    if (!user.is_super_admin) throw new Error("Super admin access required.");
    // On cookie-session auth there's nothing to preserve — the admin's
    // session cookie keeps living in browser cookies. Only legacy bearer
    // logins have a token in localStorage; if present, hold onto it so
    // stop() can put it back.
    const adminToken = tokenStore.get();

    const res = await post<{
      token: string;
      expires_at: string;
      target: ImpersonationTarget;
    }>("/v1/admin/impersonate", { user_id: userId });

    const next: StoredImpersonation = {
      admin_token:    adminToken,
      admin_email:    user.email,
      admin_tenant_id: tenantStore.get(),
      target:         res.target,
      started_at:     new Date().toISOString(),
      expires_at:     res.expires_at,
    };
    writeStored(next);
    setStored(next);
    tokenStore.set(res.token);
    // Switch tenant header to the target's first org so module
    // routes (which expect X-Tenant-Id) resolve to *their* data.
    const firstOrg = res.target.organisations?.[0]?.id ?? null;
    tenantStore.set(firstOrg);
    // Force a full reload so every cached query / router state
    // re-fetches as the target user. Avoids subtle "stale data
    // from the admin's session is still on screen" bugs.
    window.location.assign("/");
  }, [user]);

  const stop = useCallback<ImpersonationContextValue["stop"]>(async () => {
    const cur = readStored();
    if (!cur) return;
    // Best-effort: tell the server to drop the impersonation token.
    try { await post("/v1/admin/impersonate/stop"); }
    catch (e) { if (!(e instanceof ApiError && e.status === 401)) throw e; }
    // Restore the admin's own session.
    tokenStore.set(cur.admin_token);
    tenantStore.set(cur.admin_tenant_id);
    writeStored(null);
    setStored(null);
    window.location.assign("/admin");
  }, []);

  const value = useMemo<ImpersonationContextValue>(() => ({
    isImpersonating: !!stored,
    target:    stored?.target    ?? null,
    startedAt: stored?.started_at ?? null,
    expiresAt: stored?.expires_at ?? null,
    adminEmail: stored?.admin_email ?? null,
    start,
    stop,
  }), [stored, start, stop]);

  return <ImpersonationCtx.Provider value={value}>{children}</ImpersonationCtx.Provider>;
}

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationCtx);
  if (!ctx) throw new Error("useImpersonation must be used inside <ImpersonationProvider>");
  return ctx;
}
